/**
 * 헤더 (디자인): 🏢 전사 › [토폴로지 뷰 ▾] · 가운데 요약 지표 · 오른쪽 스냅샷 시각 + 새로 고침.
 * - 토폴로지 뷰: 처리량 shots/min · 평균 지연 s · 오류율 % · 노드 수
 * - 조감도 뷰  : 수집 상태 배지 · 처리량 · 설비 수
 * 값은 모두 피드 스냅샷(store.summary / nodes) — 첫 스냅샷 전에는 —.
 */
import { memo, useEffect, useRef, useState } from "react";
import { formatInt, formatNumber, formatLocalDateTime } from "../../lib/format";
import { selectGeneratedAt, selectMode, selectNodes, selectSummary, selectSystemStatus, usePipelineStore, } from "../../store/pipeline-store";

const VIEWS = [
    { id: "topology", label: "토폴로지 뷰" },
    { id: "aerial", label: "조감도 뷰" },
];

const SYSTEM_BADGE = {
    healthy: { cls: "is-good", label: "수집 정상" },
    warning: { cls: "is-warn", label: "수집 주의" },
    error: { cls: "is-bad", label: "수집 장애" },
};

function ViewSelect({ view, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open)
            return undefined;
        const onDown = (e) => {
            if (ref.current && !ref.current.contains(e.target))
                setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);
    const current = VIEWS.find((v) => v.id === view) ?? VIEWS[0];
    return (<div className="dfv-select" ref={ref}>
      <button type="button" className="dfv-select__trigger" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {current.label}
        <span className="material-symbols-outlined" aria-hidden="true">{open ? "expand_less" : "expand_more"}</span>
      </button>
      {open && (<ul className="dfv-select__menu" role="listbox">
          {VIEWS.map((v) => (<li key={v.id}>
              <button type="button" role="option" aria-selected={v.id === view} className={`dfv-select__item${v.id === view ? " is-active" : ""}`} onClick={() => { setOpen(false); onChange(v.id); }}>
                {v.label}
              </button>
            </li>))}
        </ul>)}
    </div>);
}

function Stat({ label, value, unit, tone, title }) {
    return (<span className="dfv-stat" title={title}>
      <span className="dfv-stat__label">{label}</span>
      <b className={`dfv-stat__value${tone ? ` ${tone}` : ""}`}>{value}</b>
      {unit && <small className="dfv-stat__unit">{unit}</small>}
    </span>);
}

export const DashboardHeader = memo(function DashboardHeader({ view, onViewChange, onRefresh }) {
    const summary = usePipelineStore(selectSummary);
    const nodes = usePipelineStore(selectNodes);
    const generatedAt = usePipelineStore(selectGeneratedAt);
    const mode = usePipelineStore(selectMode);
    const system = usePipelineStore(selectSystemStatus);
    const has = mode !== "idle";
    const machines = nodes.filter((n) => n.data.nodeType === "source").length;
    const tp = has ? formatNumber(summary.totalThroughput, summary.totalThroughput >= 100 ? 0 : 1) : "—";
    const badge = SYSTEM_BADGE[system] ?? SYSTEM_BADGE.healthy;

    return (<header className="dfv-header" role="banner">
      <div className="dfv-header__crumb">
        <span className="material-symbols-outlined dfv-header__icon" aria-hidden="true">domain</span>
        <span className="dfv-header__root">전사</span>
        <span className="material-symbols-outlined dfv-header__sep" aria-hidden="true">chevron_right</span>
        <ViewSelect view={view} onChange={onViewChange}/>
      </div>

      <div className="dfv-header__stats">
        {view === "topology" ? (<>
            <Stat label="처리량" value={tp} unit="shots/min" title="전체 설비 처리량 합계 (최근 10분 평균)"/>
            <Stat label="평균 지연" value={has ? formatNumber(summary.avgLatency, 0) : "—"} unit="s" title="공장별 마지막 샷 도착 후 경과(초) 평균"/>
            <Stat label="오류율" value={has ? formatNumber(summary.errorRate, 1) : "—"} unit="%" tone={summary.errorRate > 0 ? "is-bad" : ""} title="오류 상태 노드 비율"/>
            <Stat label="노드" value={has ? formatInt(nodes.length) : "—"}/>
          </>) : (<>
            {has && <span className={`dfv-badge ${badge.cls}`} title="수집기 노드 상태 기준 (오류 > 경고 > 정상)">{badge.label}</span>}
            <Stat label="처리량" value={tp} unit="shots/min" title="전체 설비 처리량 합계 (최근 10분 평균)"/>
            <Stat label="설비" value={has ? formatInt(machines) : "—"} unit="대"/>
          </>)}
      </div>

      <div className="dfv-header__right">
        <span className="dfv-header__time" title="마지막 스냅샷 시각">{generatedAt ? formatLocalDateTime(generatedAt) : "—"}</span>
        <button type="button" className="dfv-icon-btn dfv-icon-btn--lg" aria-label="새로 고침" title="새로 고침" onClick={onRefresh}>
          <span className="material-symbols-outlined" aria-hidden="true">refresh</span>
        </button>
      </div>
    </header>);
});
export default DashboardHeader;
